import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import { AuthService } from '@services';
import {
    DxButtonModule,
    DxDropDownButtonModule,
    DxToolbarModule,
} from 'devextreme-angular';
import {
    PermissionType,
    AccessType,
    EmptyCheckInAction,
} from '@services/web-api-client';

interface MenuAction {
    id: EmptyCheckInAction;
    text: string;
    icon: string;
}

@Component({
    selector: 'app-empty-check-in-toolbar',
    standalone: true,
    imports: [DxToolbarModule, DxButtonModule, DxDropDownButtonModule],
    templateUrl: './empty-check-in-toolbar.component.html',
})
export class EmptyCheckInToolbarComponent implements OnChanges {
    @Input() number: string | null = null;
    @Input() action: EmptyCheckInAction[] | null = null;
    @Input() isReadOnly: boolean = false;
    @Input() isSubmitting: boolean = false;
    @Input() isLoading: boolean = false;

    @Output() cancelClick = new EventEmitter<void>();
    @Output() shuntMoveClick = new EventEmitter<void>();
    @Output() save = new EventEmitter<EmptyCheckInAction>();

    protected auth = inject(AuthService);
    protected moreMenuActions: MenuAction[] = [];
    public EmptyCheckInAction = EmptyCheckInAction;

    private readonly allMenuActions: MenuAction[] = [
        {
            id: EmptyCheckInAction.ShuntMove,
            text: 'Shunt Move Pending',
            icon: 'cursormove',
        },
        {
            id: EmptyCheckInAction.Investigate,
            text: 'Investigate',
            icon: 'info',
        },
        {
            id: EmptyCheckInAction.StartLoading,
            text: 'Start Loading',
            icon: 'car',
        },
        {
            id: EmptyCheckInAction.MarkAsLoaded,
            text: 'Mark as Loaded',
            icon: 'check',
        },
        {
            id: EmptyCheckInAction.CancelMove,
            text: 'Cancel Move',
            icon: 'close',
        },
        {
            id: EmptyCheckInAction.CheckOut,
            text: 'Check Out',
            icon: 'arrowright',
        },
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['number'] || changes['action']) {
            this.buildMenuActions();
        }
    }

    private buildMenuActions(): void {
        if (this.isLoading) {
            this.moreMenuActions = [];
            return;
        }

        this.moreMenuActions = this.allMenuActions.filter((action) =>
            this.canShowButton(action.id),
        );
    }

    canShowButton(action: EmptyCheckInAction): boolean {
        if (this.isLoading) return false;

        if (!this.number && !this.isReadOnly) {
            return (
                action === EmptyCheckInAction.CheckIn &&
                this.auth.checkPermission(PermissionType.EmptyCheckIns, AccessType.Manage)
            );
        }

        if (this.number && !this.isReadOnly && action === EmptyCheckInAction.Save) {
            return true;
        }

        return this.action?.includes(action) ?? false;
    }

    get showMoreMenu(): boolean {
        return this.number !== null && this.moreMenuActions.length > 0;
    }

    onMenuActionClick(e: { itemData?: MenuAction }): void {
        if (!e.itemData) return;

        switch (e.itemData.id) {
            case EmptyCheckInAction.ShuntMove:
                this.shuntMoveClick.emit();
                break;
            default:
                this.onButtonClick(e.itemData.id as EmptyCheckInAction);
        }
    }

    onButtonClick(status: EmptyCheckInAction) {
        this.save.emit(status);
    }
}
